import { useSnackbar } from 'notistack';
import { ProductSubscription, SubscriptionPaymentTerm } from '../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useApi } from '../../shared/context';
import { getResponseData } from '../../shared/utils/axios';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { QueryKeys } from '../../shared/hooks';
import { CerebroTextButton } from '../../shared/components';

interface DeleteSubscriptionPaymentTermDialogProps {
	companyGuid: string | undefined;
	dealGuid: string | undefined;
	subscription: ProductSubscription | null;
	term: SubscriptionPaymentTerm | null;
	close: () => void;
}

export const DeleteSubscriptionPaymentTermDialog: React.FC<DeleteSubscriptionPaymentTermDialogProps> = ({
	dealGuid,
	companyGuid,
	subscription,
	term,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (term !== null) deleteTerm(term);
	};

	const { mutate: deleteTerm } = useMutation<void, unknown, SubscriptionPaymentTerm>(
		termToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.Deals.SpecificPaymentTerm(dealGuid, subscription?.guid, termToDelete.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.PaymentTerms(dealGuid, subscription?.guid));
				snackbar.enqueueSnackbar('Payment term successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the deal subscription payment term.', {
					variant: 'error',
				});
			},
		}
	);

	return (
		<Dialog open={term !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>Are you sure you want to delete the subscription payment term?</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
